import React, { useState, useEffect, useMemo, memo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AppSelect } from '../../../../components/common/select';
import { validateProjectNameBlockedWords } from '../../../../utils/validation';

const ProjectSection = ({
  control,
  watch,
  formErrors,
  errors,
  handleSelectChange,
  handleTextInputChange,
  handleTextInputBlur,
  options,
  formCascadeUsers = [],
  formCascadeDepts = []
}) => {
  const [projectNameWarning, setProjectNameWarning] = useState('');
  const projectName = useWatch({ control, name: 'projectName' });
  const watchedDepartment = useWatch({ control, name: 'departmentId' });

  // Cascade Assigned To by selected department using new HRIS data (formCascadeUsers)
  // Falls back to full list when no department selected or no matching users found
  const cascadedAssignedUserOptions = useMemo(() => {
    return formCascadeUsers.length > 0 ? formCascadeUsers : (options.assignedUserOptions || []);
  }, [formCascadeUsers, options.assignedUserOptions]);

  // Use HRIS dept options for display if available, else fall back to existing
  const resolvedDeptOptions = formCascadeDepts.length > 0 ? formCascadeDepts : (options.departmentOptions || []);

  // ✅ BLOCKED WORDS: Clear warning when project name is cleared or form is reset
  useEffect(() => {
    if (!projectName) {
      setProjectNameWarning('');
    } else {
      // Re-validate when projectName changes
      const warning = validateProjectNameBlockedWords(projectName);
      setProjectNameWarning(warning || '');
    }
  }, [projectName]);

  return (
    <>
      <div className="inv-section-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
        Project Information
      </div>
      <div className="row">
        <div className="col-6">
          <Controller
            name="stage"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="stage"
                label="Stage"
                value={field.value || ''}
                onChange={handleSelectChange}
                options={options.stageOptions || []}
                placeholder="Select a stage"
                required={true}
                error={formErrors?.stage || errors.stage?.message}
                disabled={!options.stageOptions || options.stageOptions.length === 0}
                valueField="value"
                labelField="label"
                searchField="label"
              />
            )}
          />
        </div>

        <div className="col-6">
          <Controller
            name="leadSource"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="leadSource"
                label="Lead Source"
                value={field.value || ''}
                onChange={handleSelectChange}
                options={options.leadSourceOptions || []}
                placeholder="Select a lead source"
                required={true}
                error={formErrors?.leadSource || errors.leadSource?.message}
                disabled={!options.leadSourceOptions || options.leadSourceOptions.length === 0}
                valueField="value"
                labelField="label"
                searchField="label"
              />
            )}
          />
        </div>

        <div className="col-6">
          <Controller
            name="projectName"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="projectName" className="form-label">Project Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  {...field}
                  className={`form-control ${formErrors?.projectName || errors.projectName ? 'is-invalid' : ''} ${projectNameWarning ? 'is-warning' : ''}`}
                  id="projectName"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                    // ✅ BLOCKED WORDS: Check for blocked words in real-time
                    const warning = validateProjectNameBlockedWords(e.target.value);
                    setProjectNameWarning(warning || '');
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter project name"
                  autoComplete="off"
                />
                {projectNameWarning && (
                  <div className="text-warning small mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-alert-triangle me-1" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                      <path d="M12 9v4"></path>
                      <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z"></path>
                      <path d="M12 16h.01"></path>
                    </svg>
                    {projectNameWarning}
                  </div>
                )}
                {(formErrors?.projectName || errors.projectName) && (
                  <div className="invalid-feedback">
                    {formErrors?.projectName || errors.projectName?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <div className="col-6 mb-3">
          <Controller
            name="projectDescription"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="projectDescription" className="form-label">
                  Project Description <span className="text-danger">*</span>
                </label>
                <textarea
                  {...field}
                  className={`form-control ${formErrors?.projectDescription || errors.projectDescription ? 'is-invalid' : ''}`}
                  id="projectDescription"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter project description"
                  autoComplete="off"
                />
                {(formErrors?.projectDescription || errors.projectDescription) && (
                  <div className="invalid-feedback">
                    {formErrors?.projectDescription || errors.projectDescription?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <div className="col-6">
          <Controller
            name="assignedUser"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="assignedUser"
                label="Assigned To"
                value={field.value || ''}
                onChange={handleSelectChange}
                options={cascadedAssignedUserOptions}
                placeholder="Select an assigned user"
                required={true}
                error={formErrors?.assignedUser || errors.assignedUser?.message}
                disabled={cascadedAssignedUserOptions.length === 0}
                valueField="value"
                labelField="label"
                searchField="label,email"
              />
            )}
          />
        </div>

        <div className="col-6">
          <Controller
            name="competitor"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="competitor"
                label="Competitor"
                value={field.value || []}
                onChange={handleSelectChange}
                options={options.competitorOptions || []}
                placeholder="Select or add competitors"
                required={false}
                error={formErrors?.competitor || errors.competitor?.message}
                disabled={false}
                valueField="value"
                labelField="label"
                searchField="label"
                multiple={true}
                create={true}
              />
            )}
          />
        </div>

        <div className="col-6">
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="departmentId"
                label="Department"
                value={field.value || ''}
                onChange={handleSelectChange}
                options={resolvedDeptOptions}
                placeholder="Select a department"
                required={false}
                error={formErrors?.departmentId || errors.departmentId?.message}
                valueField="value"
                labelField="label"
                searchField="label"
              />
            )}
          />
        </div>
      </div>
    </>
  );
};

export default memo(ProjectSection);